import {
  Close as CloseIcon,
  ViewWeek as ViewWeekIcon,
} from '@mui/icons-material';
import { Button, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { every, isBoolean, isEmpty, transform } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikCheckbox, FormikPanel, FormikSelectOption } from 'components';
import { TranslationNamespace } from 'i18n';
import { VisibleColumns } from 'types';

type Props = {
  title?: string;
  visibleColumns: VisibleColumns;
  availableColumns: FormikSelectOption[];
  onReset: () => void;
  onSubmit: (values: any) => void;
};

export const ColumnsDropdown: React.FC<Props> = ({
  title,
  visibleColumns,
  availableColumns,
  onReset,
  onSubmit,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.columns_dropdown',
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = useMemo(() => !!anchorEl, [anchorEl]);

  const initialValues = useMemo(
    () =>
      transform(
        availableColumns,
        (acc, column) => {
          acc[column.value] = isBoolean(visibleColumns?.[column.value])
            ? visibleColumns[column.value]
            : true;
        },
        {} as VisibleColumns,
      ),
    [availableColumns, visibleColumns],
  );

  const sortingDirty = useMemo(
    () => !every(initialValues, (value) => value),
    [initialValues],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReset = useCallback(() => {
    onReset();
    handleClose();
  }, [handleClose, onReset]);

  const handleSubmit = useCallback(
    (values: VisibleColumns, helpers: FormikHelpers<VisibleColumns>) => {
      helpers.setSubmitting(false);
      onSubmit(values);
      handleClose();
    },
    [handleClose, onSubmit],
  );

  const validate = useCallback((values: VisibleColumns) => {
    const unselectedAll = Object.values(values).every((value) => !value);
    if (unselectedAll) {
      return {
        emptyColumns: true,
      };
    }
  }, []);

  const handleSelectAllColumns = useCallback(
    (setValues: any, checked: boolean) => {
      const updatedValue = transform(
        availableColumns,
        (acc, column) => {
          acc[column.value] = checked;
        },
        {} as VisibleColumns,
      );
      setValues(updatedValue);
    },
    [availableColumns],
  );

  return (
    <div>
      <IconButton onClick={handleClick}>
        <ViewWeekIcon color={sortingDirty ? 'info' : 'inherit'} />
      </IconButton>
      <FormikPanel open={open} anchorEl={anchorEl} onClose={handleClose}>
        <div>
          <div className="tw-flex tw-items-center">
            <div className="tw-text-lg tw-font-bold tw-flex-1">
              {title || t('title')}
            </div>
            <CloseIcon className="tw-cursor-pointer" onClick={handleClose} />
          </div>
          <Formik
            initialValues={initialValues}
            validate={validate}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, errors, setValues }) => (
              <Form>
                <div className="tw-font-bold tw-my-4">{`${t(
                  'selected_count',
                )}: ${Object.values(values).filter(Boolean).length}`}</div>

                <div className="tw-grid tw-gap-2 tw-my-4">
                  <FormControlLabel
                    key="columns"
                    sx={{ fontWeight: 'bold' }}
                    control={
                      <Field
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleSelectAllColumns(setValues, e.target.checked)
                        }
                        component={Checkbox}
                        type="checkbox"
                        checked={
                          availableColumns.length ===
                          Object.values(values).filter(Boolean).length
                        }
                      />
                    }
                    disableTypography
                    label={t('select_all')}
                  />
                  {availableColumns.map((column) => (
                    <FormikCheckbox
                      key={column.value}
                      name={column.value}
                      label={column.label}
                    />
                  ))}
                </div>
                <div className="tw-my-4 tw-grid tw-grid-cols-2 tw-gap-2">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleReset}
                  >
                    {t('buttons.reset')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    type="submit"
                    disabled={!isEmpty(errors)}
                  >
                    {t('buttons.submit')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </FormikPanel>
    </div>
  );
};
